import React, { useState, forwardRef } from 'react';
import Select, { StylesConfig, SingleValue } from 'react-select';
import { useB2BTranslation } from '../../hooks/useB2BTranslation';
import { Eye, EyeOff } from 'lucide-react';
import { B2B_PHONE_COUNTRY_CODES_FULL } from '../../constants/B2BCountryCodes';

/** Option shape used by react-select for the phone country-code dropdown */
interface PhoneCodeOption {
  value: string;  // e.g. "+44"
  label: string;  // e.g. "🇬🇧 +44 United Kingdom"
  flag: string;
  name: string;
}

/** Build react-select option list once from the full country list */
const PHONE_CODE_OPTIONS: PhoneCodeOption[] = B2B_PHONE_COUNTRY_CODES_FULL.map(c => ({
  value: c.code,
  label: `${c.flag} ${c.code} ${c.country}`,
  flag: c.flag,
  name: c.country,
}));

/** react-select styles that match the existing country-code-select design */
const phoneSelectStyles: StylesConfig<PhoneCodeOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: '44px',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    borderRadius: '0.375rem',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    flexShrink: 0,
  }),
  valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
  singleValue: (base) => ({ ...base, fontSize: '0.875rem' }),
  input: (base) => ({ ...base, fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: '0.875rem', minWidth: '220px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#f3f4f6' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    cursor: 'pointer',
    padding: '0.4rem 0.75rem',
  }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 0.25rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

export interface B2BFormFieldProps {
  name: string;
  label: string;
  type: 'text' | 'email' | 'tel' | 'url' | 'textarea' | 'select' | 'phone' | 'checkbox' | 'checkbox-group' | 'date' | 'password';
  value: string | string[] | boolean;
  onChange: (name: string, value: string | string[] | boolean) => void;
  onBlur?: (nameOrEvent: string | React.FocusEvent<HTMLElement>) => void;
  required?: boolean;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
  options?: Array<{ value: string; label: string }>;
  rows?: number;
  maxLength?: number;
  min?: string;
  max?: string;
  id?: string;
  'aria-describedby'?: string;
  showPasswordToggle?: boolean;
  passwordRequirements?: {
    minLength?: number;
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumber?: boolean;
    requireSpecialChar?: boolean;
  };
  confirmPassword?: boolean;
}

/**
 * Consolidated B2B Form Field Component
 * Replaces duplicate form field patterns across B2B components
 */
export const B2BFormField = forwardRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, B2BFormFieldProps>(({
  name,
  label,
  type,
  value,
  onChange,
  onBlur,
  required = false,
  placeholder,
  error,
  className = '',
  disabled = false,
  options = [],
  rows = 3,
  maxLength,
  min,
  max,
  id,
  'aria-describedby': ariaDescribedby,
  showPasswordToggle = false,
  passwordRequirements,
  confirmPassword = false
}, ref) => {
  const { t } = useB2BTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (type === 'checkbox-group') {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(e.target.value)
        ? currentValues.filter(v => v !== e.target.value)
        : [...currentValues, e.target.value];
      onChange(name, newValues);
    } else {
      onChange(name, e.target.value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (onBlur && e && e.target) {
      // Pass the event to the handler only if it's valid
      try {
        onBlur(e as any);
      } catch (error) {
        console.warn('Error in handleBlur:', error);
      }
    }
  };

  const handleCheckboxChange = (optionValue: string) => {
    const currentValues = Array.isArray(value) ? value : [];
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter(v => v !== optionValue)
      : [...currentValues, optionValue];
    onChange(name, newValues);
  };

  const getFieldId = () => id || name;
  const getFieldClassName = () => {
    const baseClass = type === 'textarea' ? 'form-textarea' : 
                     type === 'select' ? 'form-select' : 
                     type === 'phone' ? 'phone-number-input' : 'form-input';
    const errorClass = error ? 'error' : '';
    return `${baseClass} ${errorClass} ${className}`.trim();
  };

  const renderInput = () => {
    const fieldId = getFieldId();
    const fieldClassName = getFieldClassName();
    const commonProps = {
      id: fieldId,
      name,
      value: typeof value === 'string' ? value : '',
      onChange: handleChange,
      onBlur: handleBlur,
      placeholder,
      className: fieldClassName,
      required,
      disabled,
      'aria-invalid': error ? true : false,
      'aria-describedby': ariaDescribedby || (error ? `${fieldId}-error` : undefined)
    };

    switch (type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={rows}
            maxLength={maxLength}
            ref={ref as React.LegacyRef<HTMLTextAreaElement>}
          />
        );
      
      case 'select':
        return (
          <select {...commonProps} ref={ref as React.LegacyRef<HTMLSelectElement>}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'phone': {
        // Parse the combined value "+CC NNNNNN" into separate parts
        // This is the SINGLE source of truth for country code and phone number
        const strValue = typeof value === 'string' ? value : '';
        let selectCC = '+44'; // default
        let inputPhone = '';
        
        if (strValue.includes(' ')) {
          // Standard combined format: "+44 7956468151"
          const spaceIdx = strValue.indexOf(' ');
          const ccPart = strValue.substring(0, spaceIdx);
          if (ccPart.startsWith('+')) selectCC = ccPart;
          inputPhone = strValue.substring(spaceIdx + 1);
        } else if (strValue.startsWith('+')) {
          // No space but starts with +, try to extract cc
          const match = strValue.match(/^(\+\d{1,3})(.*)$/);
          if (match) {
            selectCC = match[1];
            inputPhone = match[2];
          }
        } else {
          // Plain number, no country code
          inputPhone = strValue;
        }
        
        const selectedOption = PHONE_CODE_OPTIONS.find(o => o.value === selectCC) ?? null;

        return (
          <div className="phone-input-container">
            <Select<PhoneCodeOption, false>
              inputId={`${fieldId}-cc`}
              name={`${name}CountryCode`}
              options={PHONE_CODE_OPTIONS}
              value={selectedOption}
              onChange={(opt: SingleValue<PhoneCodeOption>) => {
                const newCC = opt?.value ?? '+44';
                onChange(name, inputPhone ? `${newCC} ${inputPhone}` : newCC);
              }}
              isSearchable
              isDisabled={disabled}
              placeholder="🌐"
              styles={phoneSelectStyles}
              classNamePrefix="phone-cc"
              aria-label="Country code"
              menuPlacement="auto"
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            <input
              {...commonProps}
              type="tel"
              value={inputPhone}
              onChange={(e) => {
                const newPhoneNumber = e.target.value.trim();
                onChange(name, `${selectCC} ${newPhoneNumber}`);
              }}
              ref={ref as React.LegacyRef<HTMLInputElement>}
            />
          </div>
        );
      }
      
      case 'checkbox':
        return (
          <label className="checkbox-item">
            <input
              type="checkbox"
              name={name}
              checked={Boolean(value)}
              onChange={(e) => onChange(name, e.target.checked)}
              className="checkbox-input"
              required={required}
              disabled={disabled}
            />
            <span>{label}</span>
          </label>
        );
      
      case 'checkbox-group':
        return (
          <div className="checkbox-group">
            {options.map((option) => (
              <label key={option.value} className="checkbox-item">
                <input
                  type="checkbox"
                  name={`${name}[]`}
                  value={option.value}
                  checked={Array.isArray(value) ? value.includes(option.value) : false}
                  onChange={() => handleCheckboxChange(option.value)}
                  className="checkbox-input"
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <input
            {...commonProps}
            type="date"
            min={min}
            max={max}
            value={typeof value === 'string' && value.includes('T') 
              ? value.split('T')[0] 
              : (typeof value === 'string' ? value : '')}
            ref={ref as React.LegacyRef<HTMLInputElement>}
          />
        );
      
      case 'password':
        return (
          <div className="password-input-container">
            <input
              {...commonProps}
              type={showPassword ? 'text' : 'password'}
              maxLength={maxLength}
              ref={ref as React.LegacyRef<HTMLInputElement>}
            />
            {showPasswordToggle && (
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="password-toggle"
                tabIndex={-1}
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            )}
          </div>
        );
      
      default:
        return (
          <input
            {...commonProps}
            type={type}
            maxLength={maxLength}
            ref={ref as React.LegacyRef<HTMLInputElement>}
          />
        );
    }
  };

  return (
    <div className={`form-group ${className}`}>
      {type === 'checkbox' ? (
        <>
          {renderInput()}
          {error && (
            <div id={`${getFieldId()}-error`} className="field-error" role="alert">
              {error}
            </div>
          )}
        </>
      ) : (
        <>
          <label htmlFor={getFieldId()} className={required ? 'required' : ''}>
            {label}
            {required && <span className="required-asterisk" style={{ color: 'red' }}> *</span>}
          </label>
          {renderInput()}
          {error && (
            <div id={`${getFieldId()}-error`} className="field-error" role="alert">
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
});

B2BFormField.displayName = 'B2BFormField';

export default B2BFormField;
